import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Send as SendIcon, ShieldCheck, UserPlus, CheckCircle2, Info, HandCoins } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader, Spinner, AuthedAvatar, BlueCheck } from "../components/common";
import PinModal from "../components/PinModal";

export default function JackSend() {
  const nav = useNavigate();

  const [mode, setMode] = useState("send"); // "send" | "request"
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [idemKey] = useState(() => `js_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
  const [pinRequired, setPinRequired] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const isRequest = mode === "request";

  useEffect(() => { api.get("/security/pin").then(({ data }) => setPinRequired(data.pin_set && data.pin_enabled)).catch(() => {}); }, []);

  const switchMode = (m) => {
    if (m === mode) return;
    setMode(m); setAmount(""); setNote("");
  };

  const search = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true); setRecipient(null); setNotFound(false);
    try {
      const { data } = await api.post("/jacksend/lookup", { query: query.trim() });
      if (data.found) setRecipient(data.recipient);
      else setNotFound(true);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSearching(false); }
  };

  const doSend = async () => {
    setShowPin(false);
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/jacksend/send",
        { query: query.trim(), amount: amt, note: note || null },
        { headers: { "Idempotency-Key": idemKey } });
      setDone({ kind: "send", ...data });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "NOT_ON_JACKCASH") { setRecipient(null); setNotFound(true); }
      toast.error(errMsg(err));
    } finally { setSending(false); }
  };

  const doRequest = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    setSending(true);
    try {
      await api.post("/requests", { cash_tag: recipient.cash_tag, amount: amt, note: note || null });
      setDone({ kind: "request", amount_display: `$${amt.toFixed(2)}`, recipient });
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSending(false); }
  };

  const submit = () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    if (isRequest) { doRequest(); return; }
    if (pinRequired) { setShowPin(true); return; }
    doSend();
  };

  const resetAll = () => { setDone(null); setRecipient(null); setQuery(""); setAmount(""); setNote(""); setNotFound(false); };

  if (done) {
    const isReq = done.kind === "request";
    return (
      <div className="animate-fade-up">
        <PageHeader title="Jack Send" />
        <div className="text-center py-10" data-testid="jacksend-success">
          <div className="mx-auto mb-4 grid place-items-center w-16 h-16 rounded-full bg-success/10 text-success">
            {isReq ? <HandCoins size={34} /> : <CheckCircle2 size={34} />}
          </div>
          <h2 className="font-display text-2xl font-semibold">
            {isReq ? `Requested ${done.amount_display}` : `${done.amount_display} sent`}
          </h2>
          <p className="text-muted-foreground mt-1">
            {isReq ? "from" : "to"} {done.recipient.display_name} ({done.recipient.cash_tag})
          </p>
          {isReq && <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto">They'll get a notification to pay you. You'll be notified when it's paid.</p>}
          <div className="flex gap-3 mt-8">
            <button data-testid="jacksend-again" onClick={resetAll}
              className="flex-1 h-12 rounded-xl bg-secondary font-semibold">{isReq ? "New request" : "Send again"}</button>
            <button data-testid="jacksend-home" onClick={() => nav("/app")} className="flex-1 h-12 rounded-xl bg-primary text-primary-foreground font-semibold">Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-up" data-testid="jacksend-page">
      <PageHeader title="Jack Send" subtitle="Send or request by cashtag, phone or email · Worldwide" />

      {/* Send / Request toggle */}
      <div>
        <form onSubmit={search} className="relative mb-3">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="jacksend-query-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Cashtag, phone or email" inputMode="text"
            className="w-full h-14 pl-11 pr-28 rounded-2xl bg-secondary outline-none focus:ring-2 focus:ring-primary/30" />
          <button type="submit" data-testid="jacksend-search-btn" disabled={searching || !query.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-10 px-4 rounded-xl bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-50">
            {searching ? <Spinner className="w-4 h-4" /> : "Find"}
          </button>
        </form>

        {/* Send / Request toggle — compact, below search */}
        <div className="inline-flex p-0.5 rounded-xl bg-secondary mb-4" data-testid="jacksend-mode-toggle">
          <button data-testid="jacksend-mode-send" onClick={() => switchMode("send")}
            className={`h-8 px-3.5 rounded-lg text-[12px] font-semibold flex items-center gap-1.5 transition-colors ${!isRequest ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground"}`}>
            <SendIcon size={13} /> Send
          </button>
          <button data-testid="jacksend-mode-request" onClick={() => switchMode("request")}
            className={`h-8 px-3.5 rounded-lg text-[12px] font-semibold flex items-center gap-1.5 transition-colors ${isRequest ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground"}`}>
            <HandCoins size={13} /> Request
          </button>
        </div>

        {notFound && (
          <div data-testid="jacksend-not-found" className="rounded-2xl border border-dashed border-border p-6 text-center">
            <div className="mx-auto mb-3 grid place-items-center w-12 h-12 rounded-full bg-secondary text-primary"><UserPlus size={22} /></div>
            <p className="text-sm font-medium">Not on Jack Cash yet</p>
            <p className="text-sm text-muted-foreground mt-1">We couldn't find an account for “{query}”. Invite them to join Jack Cash so you can {isRequest ? "request" : "send"} money.</p>
          </div>
        )}

        {recipient && (
          <div data-testid="jacksend-recipient" className="rounded-3xl bg-card border border-border p-5 mb-4">
            <div className="flex items-center gap-3">
              <AuthedAvatar src={recipient.profile_photo} name={recipient.display_name} size={56} className="rounded-full" />
              <div className="min-w-0">
                <div className="font-display font-semibold flex items-center gap-1.5">
                  {recipient.display_name}
                  <BlueCheck show={recipient.blue_check} size={16} testid="jacksend-recipient-bluecheck" />
                </div>
                <div className="text-sm text-muted-foreground font-mono">{recipient.cash_tag}</div>
              </div>
            </div>

            {recipient.blue_check ? (
              <div data-testid="jacksend-protected" className="flex items-center gap-2 mt-4 rounded-xl bg-success/10 border border-success/20 px-3.5 py-2.5">
                <ShieldCheck size={16} className="text-success shrink-0" />
                <p className="text-[12px] font-medium text-success leading-snug">Verified &amp; Protected by Jack Cash + Guard</p>
              </div>
            ) : (
              <div data-testid="jacksend-unprotected" className="flex items-start gap-2 mt-4 rounded-xl bg-warning/10 border border-warning/20 px-3.5 py-2.5">
                <Info size={15} className="text-warning shrink-0 mt-0.5" />
                <p className="text-[12px] font-medium text-warning leading-snug">This account isn't blue-check verified. {isRequest ? "Request" : "Send"} only if you know and trust them.</p>
              </div>
            )}

            <div className="mt-5">
              <label className="text-xs text-muted-foreground">Amount</label>
              <div className="relative mt-1">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-semibold text-muted-foreground">$</span>
                <input data-testid="jacksend-amount-input" type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00" className="w-full h-16 pl-10 pr-4 rounded-2xl bg-secondary outline-none text-2xl font-semibold focus:ring-2 focus:ring-primary/30" />
              </div>
            </div>
            <input data-testid="jacksend-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What's it for? (optional)"
              className="w-full h-12 px-4 mt-3 rounded-xl bg-secondary outline-none" />

            {isRequest ? (
              <div data-testid="jacksend-request-notice" className="flex items-start gap-2 mt-4 rounded-xl bg-secondary/60 px-3.5 py-3">
                <Info size={15} className="text-primary mt-0.5 shrink-0" />
                <p className="text-[12px] text-muted-foreground leading-snug">
                  We'll send <strong className="text-foreground">{recipient.display_name}</strong> a request to pay you. No money moves until they approve it.
                </p>
              </div>
            ) : (
              <div data-testid="jacksend-zelle-notice" className="flex items-start gap-2 mt-4 rounded-xl bg-secondary/60 px-3.5 py-3">
                <Info size={15} className="text-primary mt-0.5 shrink-0" />
                <p className="text-[12px] text-muted-foreground leading-snug">
                  Only send money to people you know and trust. Transfers are instant and typically <strong className="text-foreground">can't be canceled</strong> once sent.
                </p>
              </div>
            )}

            <button data-testid="jacksend-send-btn" onClick={submit} disabled={sending}
              className="w-full h-14 mt-4 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-transform active:scale-[0.98]">
              {sending ? <Spinner className="w-5 h-5" /> : (isRequest ? <><HandCoins size={18} /> Request money</> : <><SendIcon size={18} /> Send money</>)}
            </button>
          </div>
        )}
      </div>

      <PinModal open={showPin} onClose={() => setShowPin(false)} onSuccess={doSend}
        title="Confirm with your PIN" subtitle={`Sending $${parseFloat(amount || 0).toFixed(2)}`} />
    </div>
  );
}
